import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Bell, ArrowLeft, ShoppingBag, Instagram } from 'lucide-react';
import { pushToDataLayer, trackBeginCheckout } from '../lib/analytics';
import { NextDropHero } from '../components/shop/DropCountdown';

const API = process.env.REACT_APP_BACKEND_URL;

const TeeCard = ({ item }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [size, setSize] = useState('M');
  const [buying, setBuying] = useState(false);

  const buy = async () => {
    setBuying(true);
    pushToDataLayer('city_series_buy_click', {
      city: item.city,
      city_key: item.key,
      size,
      price: item.price,
      currency: 'USD',
    });
    trackBeginCheckout(item.price, [{
      item_id: `city-series-${item.key}`,
      item_name: item.name,
      item_variant: size,
      price: item.price,
      quantity: 1,
    }], 'city_series');
    try {
      const res = await fetch(`${API}/api/city-series/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: item.key, size, quantity: 1 }),
      });
      if (!res.ok) throw new Error('failed');
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      setBuying(false);
      setState('error');
    }
  };

  const join = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState('sending');
    try {
      const res = await fetch(`${API}/api/leads/dojo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          magnet_id: 'city_series_waitlist',
          source: `city-series-${item.key}`,
          metadata: { city: item.city },
        }),
      });
      if (!res.ok) throw new Error('failed');
      setState('done');
      pushToDataLayer('waitlist_join', {
        waitlist: 'city_series',
        city: item.city,
        city_key: item.key,
        source: `city-series-${item.key}`,
      });
    } catch (err) {
      setState('error');
    }
  };

  return (
    <div className="group bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors flex flex-col" data-testid={`city-tee-card-${item.key}`}>
      <div className="relative aspect-square overflow-hidden bg-zinc-900">
        <img
          src={item.image}
          alt={`${item.name} — NinjAthlete City Series`}
          className={`w-full h-full object-cover transition-opacity duration-300 ${item.back_image ? 'group-hover:opacity-0' : 'group-hover:scale-105'}`}
          loading="lazy"
        />
        {item.back_image && (
          <img
            src={item.back_image}
            alt={`${item.name} — back print`}
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            loading="lazy"
            data-testid={`city-tee-back-${item.key}`}
          />
        )}
        <span className="absolute top-3 left-3 px-2 py-1 text-[10px] uppercase tracking-widest bg-black/70 text-[#C7FA3E] border border-[#C7FA3E]/30" style={{ fontFamily: 'Oswald, sans-serif' }}>
          Drop 01
        </span>
        {item.waitlist_count > 0 && (
          <span className="absolute top-3 right-3 px-2 py-1 text-[10px] uppercase tracking-wider bg-black/90 text-white border border-zinc-600" data-testid={`city-tee-count-${item.key}`}>
            {item.waitlist_count} on the list
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-white text-xl leading-tight mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
          {item.city} — {item.design}
        </h3>
        <p className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] mb-1">{item.tagline} · Limited Run</p>
        {item.run_club && (
          item.run_club_instagram ? (
            <a
              href={item.run_club_instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[#C7FA3E] hover:text-white text-[10px] uppercase tracking-[0.2em] mb-2 transition-colors"
              data-testid={`city-tee-partner-${item.key}`}
            >
              <Instagram className="w-3 h-3" /> Partnered with {item.run_club}
            </a>
          ) : (
            <p className="text-[#C7FA3E] text-[10px] uppercase tracking-[0.2em] mb-2" data-testid={`city-tee-partner-${item.key}`}>
              Partnered with {item.run_club}
            </p>
          )
        )}
        <p className="text-zinc-300 text-sm mb-4 mt-2">
          <span className="text-white font-semibold">${item.price.toFixed(0)}</span>
          <span className="text-zinc-500"> · drop price · oversized heavyweight</span>
        </p>

        <div className="mt-auto">
          {item.available ? (
            <div data-testid={`city-tee-buy-${item.key}`}>
              <div className="flex gap-1.5 mb-3 flex-wrap">
                {item.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    className={`px-2.5 py-1.5 text-xs font-bold border transition-colors ${size === s ? 'bg-[#C7FA3E] text-black border-[#C7FA3E]' : 'text-zinc-400 border-zinc-700 hover:border-zinc-500'}`}
                    style={{ fontFamily: 'Oswald, sans-serif' }}
                    data-testid={`city-tee-size-${item.key}-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <button
                onClick={buy}
                disabled={buying}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#C7FA3E] hover:bg-[#b8e01f] disabled:opacity-60 text-black text-xs font-bold uppercase tracking-widest transition-colors"
                style={{ fontFamily: 'Oswald, sans-serif' }}
                data-testid={`city-tee-buy-btn-${item.key}`}
              >
                <ShoppingBag className="w-3.5 h-3.5" /> {buying ? 'Opening checkout…' : `Buy · $${item.price.toFixed(0)}`}
              </button>
            </div>
          ) : state === 'done' ? (
            <p className="inline-flex items-center gap-2 text-[#C7FA3E] text-xs uppercase tracking-widest" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid={`city-tee-success-${item.key}`}>
              <Check className="w-4 h-4" /> You're on the list
            </p>
          ) : open ? (
            <form onSubmit={join} className="flex gap-2" data-testid={`city-tee-form-${item.key}`}>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                autoComplete="email"
                className="flex-1 min-w-0 bg-black/50 border border-zinc-700 focus:border-[#C7FA3E] px-3 py-2.5 text-sm text-white outline-none transition-colors"
                data-testid={`city-tee-email-${item.key}`}
              />
              <button
                type="submit"
                disabled={state === 'sending'}
                className="px-4 py-2.5 bg-[#C7FA3E] hover:bg-[#b8e01f] disabled:opacity-60 text-black text-xs font-bold uppercase tracking-widest transition-colors whitespace-nowrap"
                style={{ fontFamily: 'Oswald, sans-serif' }}
                data-testid={`city-tee-submit-${item.key}`}
              >
                {state === 'sending' ? '…' : 'Join'}
              </button>
            </form>
          ) : (
            <button
              onClick={() => setOpen(true)}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#C7FA3E]/40 text-[#C7FA3E] hover:bg-[#C7FA3E] hover:text-black text-xs font-bold uppercase tracking-widest transition-colors"
              style={{ fontFamily: 'Oswald, sans-serif' }}
              data-testid={`city-tee-waitlist-btn-${item.key}`}
            >
              <Bell className="w-3.5 h-3.5" /> Join the waitlist
            </button>
          )}
          {state === 'error' && (
            <p className="text-red-400 text-xs mt-2" data-testid={`city-tee-error-${item.key}`}>Something went wrong — try again.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default function CitySeriesPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'City Series — 14 Cities, 14 Designs (Drop 01) | NinjAthlete';
    fetch(`${API}/api/city-series`).then((r) => r.json()).then(setData).catch(() => {});
    return () => { document.title = 'NinjAthlete'; };
  }, []);

  return (
    <main className="pt-20 min-h-screen bg-[#050505]" data-testid="city-series-page">
      <section className="container-ninja section-spacing">
        <Link to="/shop" className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-white text-xs uppercase tracking-widest mb-8 transition-colors" data-testid="city-series-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to shop
        </Link>
        <div className="max-w-3xl">
          <p className="text-ninja-label text-[#C7FA3E] mb-4">
            {data?.status === 'live' ? 'Drop 01 · Live now' : 'Drop 01 · Waitlist open'}
          </p>
          <h1 className="text-white mb-6">
            City <span className="text-gradient-ninja">Series</span>
          </h1>
          <p className="text-zinc-400 text-lg leading-relaxed mb-3">
            Fourteen cities. Fourteen one-of-a-kind designs. Each limited-run oversized heavyweight tee is
            built around its city — its own saying, its own graphic, its own Ninja Run Club identity. No two
            cities wear the same shirt.
          </p>
          <p className="text-zinc-500 text-sm">
            Waitlist gets first access and drop-day pricing before anyone else. No spam — one email when your city drops.
          </p>
          {data?.waitlist_total > 0 && (
            <p className="text-[#C7FA3E] text-xs uppercase tracking-widest mt-4" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="city-series-total">
              {data.waitlist_total} {data.waitlist_total === 1 ? 'runner' : 'runners'} already on the list
            </p>
          )}
          <NextDropHero drop={data?.next_drop} />
        </div>
      </section>

      <section className="container-ninja pb-20">
        {!data ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#C7FA3E] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="city-series-grid">
            {data.items.map((item) => <TeeCard key={item.key} item={item} />)}
          </div>
        )}
      </section>

      <section className="container-ninja pb-20" data-testid="run-club-reel">
        <p className="text-ninja-label text-zinc-500 mb-2">Shot on the track</p>
        <h2 className="text-white text-2xl md:text-3xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
          The crew behind the drops
        </h2>
        <p className="text-zinc-500 text-sm mb-6 max-w-2xl">Ninja Run Club sessions — the training that every City Series design comes from.</p>
        <div className="flex gap-4 overflow-x-auto pb-3 snap-x snap-mandatory" data-testid="run-club-reel-strip">
          {[1, 2, 3, 4, 5].map((n) => (
            <video
              key={n}
              src={`${API}/api/community/image/ninjathlete/brand/run-club/clip-${n}.mp4`}
              poster={`${API}/api/community/image/ninjathlete/brand/run-club/clip-${n}-poster.webp`}
              muted
              loop
              playsInline
              autoPlay
              preload="metadata"
              className="h-[420px] md:h-[480px] aspect-[9/16] object-cover shrink-0 snap-start border border-zinc-800 bg-zinc-900"
              data-testid={`run-club-clip-${n}`}
            />
          ))}
        </div>
      </section>
    </main>
  );
}
